import {useEffect, useMemo, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Alert, DatePicker, Select, Spin, Switch, Tooltip} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import moment from 'moment';
import filterIcon from 'icons/filter.svg';
import {ExchangeScopeSelector} from 'pages/exchanges/ExchangeScopeSelector';
import {useExchangeScope} from 'pages/exchanges/useExchangeScope';
import {MetricsFilter} from './MetricsFilter';
import {GET_ORDERS_METRICS} from './resolvers';
import {buildMetricsInput, metricsFilterChips} from './metricsInput';
import {currenciesIn} from './Money';
import {
  FailuresSection,
  FeesSection,
  FlowSection,
  LatencySection,
  ParticipationSection,
  QualitySection,
  SettlementSection,
} from './MetricsSections';
import {MetricsHero} from './MetricsHero';
import {Highlight, SectionCard} from './SectionCard';
import {ICONS, sectionHighlights} from './sectionMeta';
import styles from './ordersMetrics.module.scss';

/**
 * Trading View → Orders → Metrics.
 *
 * Its own tab rather than a strip inside the queues, because
 * `getOrdersMetrics` has no status filter — it reports every lifecycle stage
 * at once. Dropped into a status-scoped queue it would show the same numbers
 * on all six tabs, which reads as broken.
 *
 * It uses the same `OrderFilter` panel and the same exchange lens as the
 * queues so the filtering behaves identically, maps across every dimension the
 * metrics query supports, and says plainly which applied filters it cannot
 * honour rather than implying the figures are narrower than they are.
 */

const DATE_FIELDS = [
  {value: 'CREATED_AT', label: 'Created'},
  {value: 'PLACED_AT', label: 'Placed'},
  {value: 'LAST_FILL_DATE', label: 'Last filled'},
  {value: 'SETTLEMENT_DATE', label: 'Settled'},
];

const BROKER_MATCH = [
  {value: 'CURRENT', label: 'Currently held'},
  {value: 'EVER_INVOLVED', label: 'Ever sent'},
];

/** Each call is a fresh aggregation, so filter changes are debounced. */
const DEBOUNCE_MS = 500;

/** The headline figures a collapsed section header shows. */
const renderHighlights = items =>
  (items || []).map(item => <Highlight key={item.label} {...item} />);

export const OrdersMetricsTab = () => {
  const scope = useExchangeScope();

  const [filterValues, setFilterValues] = useState({});
  const [user, setUser] = useState(null);
  const [openFilterModal, setOpenFilterModal] = useState(false);

  // Metrics-only settings the order filter has no field for.
  const [controls, setControls] = useState({
    dateField: 'CREATED_AT',
    // An explicit range beats relying on a default the server may apply.
    startDate: moment().subtract(30, 'days').startOf('day'),
    endDate: moment().endOf('day'),
    assetCurrency: undefined,
    includeDerivatives: false,
    brokerMatch: 'CURRENT',
  });

  const input = useMemo(
    () =>
      buildMetricsInput({
        filterValues,
        scopedExchangeId: scope.exchangeId,
        userId: typeof user === 'string' ? user : user?.id,
        controls,
      }),
    [filterValues, scope.exchangeId, user, controls],
  );

  // Debounced copy — the query runs off this, not off `input`.
  const [debouncedInput, setDebouncedInput] = useState(input);
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      setDebouncedInput(input);
      return undefined;
    }
    const timer = setTimeout(() => setDebouncedInput(input), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const {data, loading, error} = useQuery(GET_ORDERS_METRICS, {
    variables: {input: debouncedInput},
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const payload = data?.getOrdersMetrics;
  const unionError = payload?.__typename === 'Error' ? payload : null;
  const metrics = unionError ? null : payload?.data;

  const chips = metricsFilterChips(filterValues, user);

  // Which currencies the scope actually spans. One means the effective rate is
  // available; several mean money stays split and the rate reads "—".
  const currencies = useMemo(
    () =>
      currenciesIn(
        metrics?.flow?.turnover,
        metrics?.fees?.charged,
        metrics?.fees?.quoted,
      ),
    [metrics],
  );
  const singleCurrency =
    Boolean(controls.assetCurrency) || currencies.length === 1;

  const highlights = useMemo(
    () => sectionHighlights(metrics, controls.assetCurrency),
    [metrics, controls.assetCurrency],
  );

  const setControl = (key, value) =>
    setControls(previous => ({...previous, [key]: value}));

  const removeChip = key => {
    if (key === '__user') return setUser(null);
    setFilterValues(previous => {
      const next = {...previous};
      delete next[key];
      return next;
    });
  };

  const clearAll = () => {
    setFilterValues({});
    setUser(null);
  };

  return (
    <div className={styles.tab}>
      <div className={styles.toolbar}>
        <div className={styles.controlGroup}>
          <label className={styles.control}>
            <span>Applies to</span>
            <Tooltip title="Which timestamp the date range filters on. Ordered in a month and settled in it are different sets of orders.">
              <Select
                size="small"
                value={controls.dateField}
                onChange={value => setControl('dateField', value)}
                options={DATE_FIELDS}
                className={styles.controlSelect}
              />
            </Tooltip>
          </label>

          <label className={styles.control}>
            <span>Range</span>
            <DatePicker.RangePicker
              size="small"
              allowClear={false}
              value={[controls.startDate, controls.endDate]}
              onChange={range => {
                setControl('startDate', range?.[0]);
                setControl('endDate', range?.[1]);
              }}
            />
          </label>
        </div>

        <div className={styles.controlGroup}>
          <label className={styles.control}>
            <span>Currency</span>
            <Tooltip title="Money is reported per currency and is never summed across them. Pick one to get a single headline figure and the effective fee rate.">
              <Select
                size="small"
                allowClear
                placeholder="All"
                value={controls.assetCurrency}
                onChange={value => setControl('assetCurrency', value)}
                options={currencies.map(code => ({value: code, label: code}))}
                className={styles.controlSelect}
              />
            </Tooltip>
          </label>

          {filterValues.broker && (
            <label className={styles.control}>
              <span>Broker orders</span>
              <Tooltip title="Currently held: orders this broker has now. Ever sent: every order they were ever sent, including ones since reassigned.">
                <Select
                  size="small"
                  value={controls.brokerMatch}
                  onChange={value => setControl('brokerMatch', value)}
                  options={BROKER_MATCH}
                  className={styles.controlSelectWide}
                />
              </Tooltip>
            </label>
          )}

          <label className={styles.control}>
            <Tooltip title="A derivative is the leftover of a partly filled order that was then rejected or expired — the unfilled units become their own record.">
              <span>Include derivatives</span>
            </Tooltip>
            <Switch
              size="small"
              checked={controls.includeDerivatives}
              onChange={value => setControl('includeDerivatives', value)}
            />
          </label>
        </div>

        <div className={styles.filterContainer}>
          <button
            type="button"
            className={`${styles.filterButton} ${
              chips.length ? styles.filterButtonOn : ''
            }`}
            onClick={() => setOpenFilterModal(!openFilterModal)}>
            <img src={filterIcon} alt="" />
            <span>Filter</span>
            {chips.length > 0 && (
              <span className={styles.filterCount}>{chips.length}</span>
            )}
          </button>
          <div
            style={{display: openFilterModal ? 'block' : 'none'}}
            className={styles.filterItems}>
            <MetricsFilter
              open={openFilterModal}
              values={filterValues}
              user={user}
              setUser={setUser}
              onApply={values => {
                setFilterValues(values || {});
                setOpenFilterModal(false);
              }}
              onReset={() => {
                setFilterValues({});
                setUser(null);
              }}
              onClose={() => setOpenFilterModal(false)}
            />
          </div>
        </div>
      </div>

      {chips.length > 0 && (
        <div className={styles.chipBar}>
          {chips.map(chip => (
            <span key={chip.key} className={styles.chip}>
              <span className={styles.chipLabel}>{chip.label}</span>
              {chip.value && (
                <span className={styles.chipValue}>{chip.value}</span>
              )}
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => removeChip(chip.key)}>
                <CloseOutlined />
              </button>
            </span>
          ))}
          <button type="button" className={styles.clearAll} onClick={clearAll}>
            Clear all
          </button>
        </div>
      )}

      <ExchangeScopeSelector {...scope} />

      {unionError || error ? (
        <Alert
          type="error"
          showIcon
          className={styles.ignoredAlert}
          message={unionError?.message || error?.message}
        />
      ) : null}

      {loading && !metrics ? (
        <div className={styles.loading}>
          <Spin size="large" />
        </div>
      ) : metrics ? (
        <div className={loading ? styles.stale : undefined}>
          <MetricsHero
            metrics={metrics}
            pinnedCurrency={controls.assetCurrency}
          />

          <div className={styles.sections}>
            <SectionCard
              icon={ICONS.flow}
              title="Flow"
              description="Volume by lifecycle stage"
              highlights={renderHighlights(highlights.flow)}
              defaultOpen>
              <FlowSection flow={metrics.flow} />
            </SectionCard>

            <SectionCard
              icon={ICONS.fees}
              title="Fees"
              description="Charged is revenue; quoted is not"
              highlights={renderHighlights(highlights.fees)}
              defaultOpen>
              <FeesSection
                fees={metrics.fees}
                singleCurrency={singleCurrency}
              />
            </SectionCard>

            <SectionCard
              icon={ICONS.settlement}
              title="Settlement"
              description="Filled but not yet delivered"
              tone={highlights.tones.settlement}
              highlights={renderHighlights(highlights.settlement)}>
              <SettlementSection
                settlement={metrics.settlement}
                inFlightCount={metrics.flow?.inFlight?.orderCount}
              />
            </SectionCard>

            <SectionCard
              icon={ICONS.latency}
              title="Latency"
              description="How fast orders complete"
              highlights={renderHighlights(highlights.latency)}>
              <LatencySection latency={metrics.latency} />
            </SectionCard>

            <SectionCard
              icon={ICONS.quality}
              title="Fill quality"
              description="Filled against what was requested"
              highlights={renderHighlights(highlights.quality)}>
              <QualitySection quality={metrics.quality} />
            </SectionCard>

            <SectionCard
              icon={ICONS.failures}
              title="Failures"
              description="What went wrong"
              tone={highlights.tones.failures}
              highlights={renderHighlights(highlights.failures)}>
              <FailuresSection failures={metrics.failures} />
            </SectionCard>

            <SectionCard
              icon={ICONS.participation}
              title="Participation"
              description="Who and what traded"
              highlights={renderHighlights(highlights.participation)}>
              <ParticipationSection participation={metrics.participation} />
            </SectionCard>
          </div>
        </div>
      ) : (
        <div className={styles.loading}>
          <p>No metrics returned for this scope.</p>
        </div>
      )}
    </div>
  );
};

export default OrdersMetricsTab;
